import React from 'react';
import { PropTypes } from 'prop-types';
import Search from './Search';
import { generateComponentDocs, generateStoryDocs } from '../../utils/documentationUtils';

export default {
  title: 'Components/SearchBar/Search',
  component: Search,
  parameters: {
    layout: 'centered',
    ...generateComponentDocs(
      // What problem does it solve?
      '\nThe `Search` component provides an interface for users to search, filter, and refine results based on specific fields. It is highly configurable and allows dynamic rendering of search fields, chips for active filters, and expandable menus for advanced searches.\n\n',
      // When & how to use it?
      '\nUse the `Search` component when a user needs to filter data using multiple fields such as text, date, dropdowns, and numeric inputs. It is designed to work with APIs or local data sources for dynamic search capabilities.\n\n',
      // Current Usage
      '\nThe `Search` component is widely used across different pages like Notes, Visits, and Routes to provide a consistent and flexible search experience.\n\n',
      // Related Components
      `\n- **[SearchChipsCarousel](http://localhost:6006/?path=/docs/components-searchbar-searchchipscarousel--docs)**: Displays active filters as chips that can be removed.
       \n- **[Button](http://localhost:6006/?path=/docs/components-button--docs)**: Used for the "Search" button inside the component.
       \n\n- \`Form Inputs\` (Includes dropdowns, date selectors, and text inputs used within the search fields):
       \n>- **[CustomSelect](http://localhost:6006/?path=/docs/components-inputs-customselect--docs)**
       \n>- **[Number](http://localhost:6006/?path=/docs/components-inputs-number--docs)**
       \n>- **[AutocompleteText](http://localhost:6006/?path=/docs/components-inputs-autocompletetext--docs)**
       \n>- **[DateSelectorV2](http://localhost:6006/?path=/docs/components-inputs-dateselectorv2--docs)**
       \n>- **[TextInput](http://localhost:6006/?path=/docs/components-inputs-textinput--docs)**
       \n\n`,
    ),
  },
  tags: ['autodocs'],
  argTypes: {
    title: {
      control: { type: 'text' },
      description: 'Title of the search button or heading when the menu is collapsed.\n\n',
      table: {
        type: { summary: 'string' },
        required: false,
      },
    },
    fields: {
      control: { type: 'array' },
      description: 'Array of field definitions for the search form. Each field defines its label, name, and type.\n\n',
      table: {
        type: { summary: 'array' },
        required: true,
      },
    },
    chipsSettings: {
      control: { type: 'object' },
      description: 'Configuration for the chips representing active filters. Each chip defines its prefix and type.\n\n',
      table: {
        type: { summary: 'object' },
        required: false,
      },
    },
    dateIconStyles: {
      control: { type: 'object' },
      description: 'Custom styles for date picker icons.\n\n',
      table: {
        type: { summary: 'object' },
        required: false,
      },
    },
  },
};

const DEFAULT_FIELDS = [];
const DEFAULT_CHIPS_SETTINGS = {};
const DEFAULT_DATE_ICON_STYLES = { bottom: 0, right: 0, marginBottom: 12 };

const SearchTemplate = ({
  title = 'Search',
  fields = DEFAULT_FIELDS,
  chipsSettings = DEFAULT_CHIPS_SETTINGS,
  dateIconStyles = DEFAULT_DATE_ICON_STYLES,
}) => {
  return (
    <div style={{ minHeight: '400px' }}>
      <Search title={title} fields={fields} chipsSettings={chipsSettings} dateIconStyles={dateIconStyles} />
    </div>
  );
};

const Template = SearchTemplate.bind({});

export const SearchRoutesFields = Template.bind({});
SearchRoutesFields.args = {
  title: 'Search routes',
  fields: [
    { label: 'Keyword', name: 'keyword', type: 'text' },
    { label: 'Name', name: 'name', type: 'text' },
    { label: 'Date From', name: 'dateFrom', type: 'date' },
    { label: 'Date To', name: 'dateTo', type: 'date' },
  ],
  chipsSettings: {
    name: {
      prefix: '',
      type: 'text',
    },
    dateFrom: {
      prefix: 'From',
      type: 'date',
    },
    dateTo: {
      prefix: 'To',
      type: 'date',
    },
  },
};
SearchRoutesFields.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to search for routes with fields like keyword, name, and date range. It includes chips for active filters.\n\n',
  // Where is it currently used on the product?
  '\nThe `Search` component in this variant is used on the Routes page for filtering route data.\n\n',
);

export const SearchTagsFields = Template.bind({});
SearchTagsFields.args = {
  title: 'Search tags',
  fields: [
    { label: 'Name', name: 'name', type: 'text' },
  ],
  chipsSettings: {
    name: {
      prefix: '',
      type: 'text',
    },
  },
};
SearchTagsFields.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to search for tags by name. It includes chips for active filters.\n\n',
  // Where is it currently used on the product?
  '\nThe `Search` component in this variant is used on the Tags page for filtering tag data.\n\n',
);

export const SearchVisits = Template.bind({});
SearchVisits.args = {
  title: 'Search visits',
  fields: [
    { label: 'Keyword', name: 'keyword', type: 'text' },
    { label: 'Date From', name: 'dateFrom', type: 'date' },
    { label: 'Date To', name: 'dateTo', type: 'date' },
    { label: 'Purpose', name: 'purpose', type: 'text' },
    { label: 'Workers Present', name: 'workersPresent', type: 'number' },
    { label: 'Workers Reached', name: 'workersReached', type: 'number' },
    {
      label: 'Status', name: 'completed', type: 'dropdown', options: ['All', 'Completed', 'Attempted'],
    },
  ],
  chipsSettings: {
    name: {
      prefix: '',
      type: 'text',
    },
    dateFrom: {
      prefix: 'From',
      type: 'date',
    },
    dateTo: {
      prefix: 'To',
      type: 'date',
    },
    purpose: {
      prefix: 'Purpose',
      type: 'text',
    },
    workersPresent: {
      prefix: 'Workers Present',
      type: 'number',
    },
    workersReached: {
      prefix: 'Workers Reached',
      type: 'number',
    },
    completed: {
      prefix: 'Visit',
      type: 'dropdown',
    },
  },
};
SearchVisits.parameters = generateStoryDocs(
  // When is this variant used?
  '\nThis variant is used to search for visits with fields like keyword, date range, purpose, and worker statistics. It includes chips for active filters.\n\n',
  // Where is it currently used on the product?
  '\nThe `Search` component in this variant is used on the Visits page for filtering visit data.\n\n',
);

SearchTemplate.propTypes = {
  title: PropTypes.string,
  fields: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
  })),
  chipsSettings: PropTypes.objectOf(PropTypes.shape({
    prefix: PropTypes.string,
    type: PropTypes.string,
  })),
  dateIconStyles: PropTypes.object,
};
